import styled from 'styled-components'
import { Form, Formik, FormikProps } from 'formik'
import { useEffect, useState } from 'react'

import AddressFormField, { FieldType } from 'checkout/components/AddressFormField'
import CountryDropdownField from 'checkout/components/CountryDropdownField'
import CreditCardInput from 'checkout/components/PaymentMethodPanel/CreditCardInput'
import Divider from 'shared/components/Divider'
import StateDropdownField from 'checkout/components/StateDropdownField'
import { Address } from 'shared/types/Address'
import { Button } from 'shared/components/Button'
import { CheckoutQA } from 'shared/dataAttributes'
import { colors } from 'shared/lib'
import { getAddressValidationSchema } from 'shared/addressValidations'
import { t } from 'localization'
import { useAddressFormContext } from 'checkout/containers/AddressFormContextProvider'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'
import { useGetAddressQuery } from 'api/addressesApi'

const TP = 'checkout.panels.PaymentMethod.NewCardForm'

export const NewCardForm = () => {
  const { addNewCreditCardRTK, order } = useCheckoutContext()
  const [isSameAsShipping, setIsSameAsShipping] = useState(true)
  const [isStripeComplete, setIsStripeComplete] = useState(false)
  const [initialValues, setInitialValues] = useState(addressDefault)
  const {
    availableCountries,
    countryISOCode,
    countryUpdateHandler,
    isStateRequired,
    isZipRequired,
  } = useAddressFormContext()

  const { data: shippingAddress, isSuccess } = useGetAddressQuery(order?.addressId)

  const handleCountryChange = (
    countryCode: string,
    currentFormValues: Partial<Address>,
    setFormValues: (values: Partial<Address>) => void,
  ) => {
    // update form fields with new country code and clear state field
    countryUpdateHandler(countryCode)
    setFormValues({ ...currentFormValues, countryCode, state: '' })
  }

  const handleFormComplete = async (isComplete: boolean, validateForm: () => void) => {
    setIsStripeComplete(isComplete)
    await validateForm()
  }

  const handleShippingAddressToggle = (
    currentFormValues: Partial<Address>,
    setFormValues: (values: Partial<Address>, shouldValidate?: boolean) => void,
  ) => {
    const mergedValues = {
      name: currentFormValues?.name || '',
      address1: shippingAddress?.address1 || '',
      address2: shippingAddress?.address2 || '',
      city: shippingAddress?.city || '',
      postalCode: shippingAddress?.postalCode || '',
      countryCode: shippingAddress?.countryCode || '',
      state: shippingAddress?.state || '',
      phone: shippingAddress?.phone || '',
    } as Partial<Address>
    const toggleIsSameAsShipping = !isSameAsShipping
    setFormValues(toggleIsSameAsShipping ? mergedValues : addressDefault, true)
    countryUpdateHandler(mergedValues.countryCode)
    setIsSameAsShipping(!isSameAsShipping)
  }

  useEffect(() => {
    if (isSuccess) {
      const address = {
        name: '',
        address1: shippingAddress?.address1 || '',
        address2: shippingAddress?.address2 || '',
        city: shippingAddress?.city || '',
        postalCode: shippingAddress?.postalCode || '',
        countryCode: shippingAddress?.countryCode || '',
        state: shippingAddress?.state || '',
        phone: shippingAddress?.phone || '',
      } as Partial<Address>
      setInitialValues(address)
    }
  }, [isSuccess])

  return (
    <CardForm>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={addNewCreditCardRTK}
        validationSchema={getAddressValidationSchema(isZipRequired, isStateRequired)}
      >
        {({ isValid, values, setValues, validateForm }: FormikProps<Address>) => {
          return (
            <Form>
              <AddressFormField
                name="name"
                qaAttr={CheckoutQA.BillingNameBox}
                title={t(`${TP}.name`, 'Name on Card')}
                type={FieldType.FullName}
              />
              <CreditCardInput
                hideIcon
                marginTopOnly
                onComplete={(isComplete) => handleFormComplete(isComplete, validateForm)}
                qaAttr={CheckoutQA.NewCreditCardBox}
                title={t(`${TP}.creditCard`, 'Credit Card')}
              />
              <CheckboxLabel>
                <CheckboxInput
                  checked={isSameAsShipping}
                  data-qa={CheckoutQA.BillingSameAsShippingCheckbox}
                  onChange={() => handleShippingAddressToggle(values, setValues)}
                  type="checkbox"
                />
                {t(`${TP}.billingSameAsShipping`, 'Billing address same as shipping')}
              </CheckboxLabel>
              {!isSameAsShipping && (
                <>
                  <BillingAddressDivider />
                  <BillingAddressHeader>
                    {t(`${TP}.billingAddress`, 'Billing Address')}
                  </BillingAddressHeader>
                  <AddressFormField
                    disabled={isSameAsShipping}
                    name="address1"
                    qaAttr={CheckoutQA.BillingAddress1Box}
                    title={t(`${TP}.streetAddress`, 'Street Address')}
                    type={FieldType.StreetAddress}
                  />
                  <AddressFormField
                    disabled={isSameAsShipping}
                    name="address2"
                    qaAttr={CheckoutQA.BillingAddress2Box}
                    title={t(`${TP}.apartment`, 'Apartment, Suite, Unit, Building, Floor, etc.')}
                    type={FieldType.UnitNumber}
                  />
                  <AddressFormField
                    disabled={isSameAsShipping}
                    name="city"
                    qaAttr={CheckoutQA.BillingCityBox}
                    title={t(`${TP}.city`, 'City')}
                    type={FieldType.City}
                  />
                  <AddressFormField
                    disabled={isSameAsShipping}
                    name="postalCode"
                    qaAttr={CheckoutQA.BillingZipCodeBox}
                    title={t(`${TP}.zipCode`, 'Zip / Postal Code')}
                    type={FieldType.ZipCode}
                  />
                  {isStateRequired && (
                    <StateDropdownField
                      countryISOCode={countryISOCode}
                      disabled={isSameAsShipping}
                      name="state"
                      qaAttr={CheckoutQA.BillingStateBox}
                      title={t(`${TP}.state`, 'State')}
                      useStateCodeAsValue
                    />
                  )}
                  <CountryDropdownField
                    availableCountries={availableCountries}
                    disabled={isSameAsShipping}
                    name="countryCode"
                    onChange={(countryCode: string) =>
                      handleCountryChange(countryCode, values, setValues)
                    }
                    qaAttr={CheckoutQA.BillingCountryBox}
                    title={t(`${TP}.countryCode`, 'Country or Region')}
                  />
                </>
              )}
              <SubmitFormButton
                $fill
                buttonType="primary1"
                disabled={!isValid || !isStripeComplete}
                qaAttr={CheckoutQA.NewCardContinueButton}
                type="submit"
              >
                {t(`${TP}.saveCard`, 'Save Card')}
              </SubmitFormButton>
            </Form>
          )
        }}
      </Formik>
    </CardForm>
  )
}

const addressDefault: Partial<Address> = {
  name: '',
  address1: '',
  address2: undefined,
  city: '',
  postalCode: '',
  countryCode: '',
  state: '',
  phone: undefined,
}

const BillingAddressDivider = styled(Divider)`
  margin: 24px 0;
`
const BillingAddressHeader = styled.div`
  color: ${colors.FC2_BLACK};
  font-weight: 500;
  margin-bottom: 20px;
`
const CardForm = styled.div`
  cursor: default;
  margin-top: 24px;
  width: 100%;
`
const CheckboxInput = styled.input`
  accent-color: ${colors.FC2_BLACK};
  height: 12px;
  margin-right: 15px;
  width: 12px;
`
const CheckboxLabel = styled.label`
  align-items: center;
  background-color: ${colors.FC2_OFF_WHITE};
  color: ${colors.FC2_DARK_GREY};
  display: flex;
  font-weight: 400;
  line-height: 14px;
  margin: 24px 0;
  padding: 16px;
  width: 100%;
`
const SubmitFormButton = styled(Button)``
